// Shared x-axis zoom across the per-corner damper charts.
//
// uPlot's cursor.sync propagates the CURSOR only, never the scale: hovering a
// synced lane moves every crosshair, but drag-zooming one lane leaves the
// others at full range. This module carries the missing half. A chart that the
// engineer drag-zooms broadcasts its new x-range to every sibling in the same
// group, and each sibling applies it to its own x-scale. A double-click
// broadcasts a reset so all synced charts return to full range together.
//
// The transport is a module-level pub/sub keyed by a group id (the same string
// the charts already use for cursor.sync, "damper-overlay"). A single
// re-entrancy guard breaks the feedback loop: when a broadcast drives a
// sibling's setScale, that sibling's own setScale hook must NOT re-broadcast,
// or two charts would ping-pong forever. Programmatic scale changes (the
// data-sync effects that re-pin x to the full window on every data refresh)
// wrap themselves in runSilently so they do not masquerade as a user zoom.

export type XRange = { min: number; max: number };

export type ZoomMember = {
  // Apply an absolute x-range, or reset to the member's own full range when
  // null. Implemented by each chart as a call to uPlot.setScale('x', ...).
  applyX: (range: XRange | null) => void;
};

const groups = new Map<string, Set<ZoomMember>>();

// True while a broadcast (or a runSilently block) is in flight. Module-global
// because broadcasts are synchronous and never interleave.
let broadcasting = false;

// The canonical group id, shared with the cursor.sync key so every chart that
// already syncs its cursor also syncs its zoom.
export const ZOOM_GROUP = "damper-overlay";

export function joinZoomGroup(group: string, member: ZoomMember): () => void {
  let set = groups.get(group);
  if (!set) {
    set = new Set();
    groups.set(group, set);
  }
  set.add(member);
  return () => {
    const s = groups.get(group);
    if (!s) return;
    s.delete(member);
    if (s.size === 0) groups.delete(group);
  };
}

// Drag-zoom: push an absolute x-range to every OTHER member of the group. The
// initiator already holds the range (uPlot set it on drag-release), so it is
// skipped. Re-entrant calls are swallowed by the guard.
export function broadcastZoom(
  group: string,
  from: ZoomMember,
  range: XRange,
): void {
  if (broadcasting) return;
  const set = groups.get(group);
  if (!set) return;
  broadcasting = true;
  try {
    for (const m of set) {
      if (m !== from) m.applyX(range);
    }
  } finally {
    broadcasting = false;
  }
}

// Double-click: reset EVERY member of the group (including the initiator) to
// its own full range. Each chart resolves "full" from its latest data.
export function broadcastReset(group: string): void {
  if (broadcasting) return;
  const set = groups.get(group);
  if (!set) return;
  broadcasting = true;
  try {
    for (const m of set) m.applyX(null);
  } finally {
    broadcasting = false;
  }
}

export function isBroadcasting(): boolean {
  return broadcasting;
}

// Run a programmatic scale change without it echoing back out as a user
// broadcast. The chart's setScale hook checks isBroadcasting() and skips while
// this block runs. Nesting is safe: an already-broadcasting call just runs fn.
export function runSilently(fn: () => void): void {
  if (broadcasting) {
    fn();
    return;
  }
  broadcasting = true;
  try {
    fn();
  } finally {
    broadcasting = false;
  }
}

// Test-only: clear all group state between cases.
export function __resetZoomGroups(): void {
  groups.clear();
  broadcasting = false;
}
